import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import {
  CalendarClock,
  CalendarOff,
  Check,
  ExternalLink,
  FileWarning,
  History,
  Image as ImageIcon,
  MapPin,
  Pencil,
  Plus,
  ShieldAlert,
  Star,
  Store,
  Telescope,
  Trash2,
  TrendingDown,
  TrendingUp,
  Unlink,
  UserRound,
  X,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { OutletMap, type OutletPoint } from "@/components/accounts/OutletMap"
import { ResearchPanel, type ResearchState } from "@/components/accounts/ResearchPanel"
import { SignalFormDialog } from "@/components/accounts/SignalFormDialog"
import { Alert } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Select } from "@/components/ui/select"
import {
  type AccountScores,
  aspectLabel,
  type CombinationAlert,
  domainLabel,
  HORIZON_LABELS,
  IMPACT_AXIS_LABELS,
  impactAxisLabel,
  type PolarityScore,
  polarityTone,
  POLARITY_LABELS,
  type Signal,
  type SignalTaxonomy,
} from "@/lib/accountSignals"
import { countdown } from "@/lib/accountDirectives"
import { cn } from "@/lib/utils"

type Account = {
  id: number
  code: string | null
  name: string
  outlet_group_key: string
  channel_group: string | null
  is_key_account: boolean
  research_enabled: boolean
  status: string
  account_group_id: number | null
  account_group_name: string | null
  pic_employee_id: number | null
  pic_name: string | null
  pic_job_title: string | null
  pic_email: string | null
}

type InternalContext = {
  outlet_count: number
  region_count: number
  by_region: { region: string; count: number }[]
}

type OutletMapData = {
  points: OutletPoint[]
  total_valid: number
  capped: boolean
  max_points: number
}

type OutletPhoto = { image_url: string; name: string | null; address: string | null }

type PicAssignment = {
  id: number
  pic_name: string | null
  pic_job_title: string | null
  started_at: string
  ended_at: string | null
  assigned_by_name: string | null
  note: string | null
  is_current: boolean
}

type Employee = { id: number; name: string; job_title: string; email: string } | null

type DirectivesSummary = {
  total_count: number
  open_count: number
  nearest_deadline: string | null
}

type PageProps = {
  account: Account
  internal_context: InternalContext
  outlet_map: OutletMapData
  outlet_photos: OutletPhoto[]
  pic_assignments: PicAssignment[]
  current_employee: Employee
  directives_summary: DirectivesSummary
  scores: AccountScores
  alerts: CombinationAlert[]
  research: ResearchState
  signals: { approved: Signal[]; pending: Signal[] }
  signal_filters: { domain: string | null; place: string | null }
  signal_filter_options: { domains: string[]; places: string[] }
  signal_taxonomy: SignalTaxonomy
}

export default function Show({
  account,
  internal_context,
  outlet_map,
  outlet_photos,
  pic_assignments,
  current_employee,
  directives_summary,
  scores,
  alerts,
  research,
  signals,
  signal_filters,
  signal_filter_options,
  signal_taxonomy,
}: PageProps) {
  const description = `Profil akun ${account.name}: identitas, PIC, konteks internal, dan timeline sinyal intelijen 360°.`

  const [formOpen, setFormOpen] = React.useState(false)
  const [editing, setEditing] = React.useState<Signal | null>(null)
  const [rejectTarget, setRejectTarget] = React.useState<Signal | null>(null)

  const openCreate = () => {
    setEditing(null)
    setFormOpen(true)
  }
  const openEdit = (signal: Signal) => {
    setEditing(signal)
    setFormOpen(true)
  }

  const applyFilter = (key: "domain" | "place", value: string) => {
    const next = {
      domain: signal_filters.domain ?? "",
      place: signal_filters.place ?? "",
      [key]: value,
    }
    router.get(`/admin/accounts/${account.id}`, next, {
      preserveState: true,
      preserveScroll: true,
      replace: true,
    })
  }

  const approve = (signal: Signal) => {
    router.patch(`/admin/accounts/signals/${signal.id}/approve`, {}, { preserveScroll: true })
  }
  const confirmReject = () => {
    if (!rejectTarget) return
    router.patch(
      `/admin/accounts/signals/${rejectTarget.id}/reject`,
      {},
      { preserveScroll: true, onFinish: () => setRejectTarget(null) },
    )
  }
  const destroy = (signal: Signal) => {
    if (!window.confirm("Hapus sinyal ini?")) return
    router.delete(`/admin/accounts/signals/${signal.id}`, { preserveScroll: true })
  }

  // Bulk selection for the "Menunggu review" (pending) panel.
  const [selected, setSelected] = React.useState<Set<number>>(new Set())
  const [bulkOpen, setBulkOpen] = React.useState(false)
  const pendingIds = signals.pending.map((s) => s.id)
  const allPendingSelected =
    pendingIds.length > 0 && pendingIds.every((id) => selected.has(id))

  // Drop ids that are no longer pending (e.g. after a delete/approve reload).
  React.useEffect(() => {
    setSelected((prev) => {
      const live = new Set(pendingIds)
      const next = new Set([...prev].filter((id) => live.has(id)))
      return next.size === prev.size ? prev : next
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signals.pending])

  const toggleOne = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const toggleAllPending = () =>
    setSelected((prev) =>
      pendingIds.every((id) => prev.has(id)) ? new Set() : new Set(pendingIds),
    )
  const bulkDestroy = () => {
    router.delete(`/admin/accounts/${account.id}/signals/bulk_destroy`, {
      data: { ids: Array.from(selected) },
      preserveScroll: true,
      onSuccess: () => {
        setSelected(new Set())
        setBulkOpen(false)
      },
    })
  }

  return (
    <>
      <Head title={account.name}>
        <meta name="description" content={description} />
        <meta property="og:title" content={account.name} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell>
        <div className="mb-2 flex items-center gap-1.5 text-xs text-ink-muted">
          <span>Outlet</span>
          <span>/</span>
          <Link href="/admin/accounts" className="hover:text-ink-body">
            Account
          </Link>
          <span>/</span>
          <span className="text-ink-body font-medium">{account.name}</span>
        </div>

        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1>{account.name}</h1>
                {account.code && <Badge tone="neutral">{account.code}</Badge>}
              </div>
              <p className="mt-1 text-sm text-ink-muted">{account.outlet_group_key}</p>
              <div className="mt-3 flex flex-wrap gap-1">
                <Badge tone={account.status === "archived" ? "muted" : "success"}>
                  {account.status === "archived" ? "Diarsipkan" : "Aktif"}
                </Badge>
                {account.is_key_account && (
                  <Badge tone="accent">
                    <Star className="mr-1 h-3 w-3" />Key Account
                  </Badge>
                )}
                {account.research_enabled && (
                  <Badge tone="signal">
                    <Telescope className="mr-1 h-3 w-3" />Riset aktif
                  </Badge>
                )}
              </div>
            </div>
            <Button asChild variant="secondary">
              <Link href={`/admin/accounts/${account.id}/edit`}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit identitas
              </Link>
            </Button>
          </div>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          {/* Identity */}
          <section className="rounded-lg border border-hairline bg-surface p-5 lg:col-span-2">
            <div className="text-sm font-medium text-ink-muted">Identitas</div>
            <dl className="mt-4 grid gap-x-6 gap-y-4 sm:grid-cols-2">
              <Field label="Holding / Grup" value={account.account_group_name} />
              <Field label="Channel Sub" value={account.channel_group} />
              <Field label="Outlet Group Key" value={account.outlet_group_key} />
              <Field label="Kode" value={account.code} />
            </dl>
          </section>

          {/* PIC */}
          <section className="rounded-lg border border-hairline bg-surface p-5">
            <div className="text-sm font-medium text-ink-muted">PIC</div>
            {account.pic_name ? (
              <div className="mt-4 flex items-start gap-3">
                <div className="rounded-full bg-page p-2">
                  <UserRound className="h-5 w-5 text-ink-muted" />
                </div>
                <div>
                  <div className="font-medium text-ink-body">{account.pic_name}</div>
                  {account.pic_job_title && (
                    <div className="text-xs text-ink-muted">{account.pic_job_title}</div>
                  )}
                  {account.pic_email && (
                    <div className="text-xs text-ink-muted">{account.pic_email}</div>
                  )}
                </div>
              </div>
            ) : (
              <p className="mt-4 text-sm text-ink-muted">Belum ada PIC. Tetapkan melalui Edit identitas.</p>
            )}
            {current_employee && (
              <p className="mt-4 border-t border-hairline pt-3 text-xs text-ink-muted">
                Anda masuk sebagai <strong>{current_employee.name}</strong>
                {current_employee.job_title ? ` · ${current_employee.job_title}` : ""}
              </p>
            )}

            {/* PIC ownership history, folded into the PIC card (#44 P2) */}
            <PicHistory assignments={pic_assignments} />
          </section>
        </div>

        {/* On-the-fly internal context */}
        <section className="mt-6 rounded-lg border border-hairline bg-surface p-5">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-ink-muted">Konteks Internal</div>
            <span className="text-xs text-ink-muted">dihitung on-the-fly dari master outlet</span>
          </div>
          <div className="mt-4 flex flex-wrap gap-6">
            <Stat icon={<Store className="h-4 w-4" />} label="Jumlah outlet" value={internal_context.outlet_count.toLocaleString("id-ID")} />
            <Stat icon={<MapPin className="h-4 w-4" />} label="Region" value={String(internal_context.region_count)} />
          </div>

          {internal_context.by_region.length > 0 ? (
            <div className="mt-5">
              <div className="text-xs font-medium text-ink-muted">Sebaran per region</div>
              <div className="mt-3 overflow-x-auto rounded-lg border border-hairline">
                <table className="w-full text-sm">
                  <thead className="bg-page text-left text-ink-muted">
                    <tr>
                      <th className="px-4 py-2 font-medium">Region</th>
                      <th className="px-4 py-2 font-medium text-right">Outlet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {internal_context.by_region.map((r) => (
                      <tr key={r.region} className="border-t border-hairline">
                        <td className="px-4 py-2 text-ink-body">{r.region}</td>
                        <td className="px-4 py-2 text-right text-ink-body">
                          {r.count.toLocaleString("id-ID")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="mt-4 text-sm text-ink-muted">
              Tidak ada outlet yang cocok dengan outlet group ini di master outlet.
            </p>
          )}
        </section>

        {/* Outlet map (P1) */}
        <section className="mt-6 rounded-lg border border-hairline bg-surface p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-medium text-ink-muted">
              <MapPin className="h-4 w-4" />
              Peta Sebaran Outlet
            </div>
            {outlet_map.points.length > 0 && (
              <span className="text-xs text-ink-muted">
                {outlet_map.capped
                  ? `Menampilkan ${outlet_map.max_points.toLocaleString("id-ID")} dari ${outlet_map.total_valid.toLocaleString("id-ID")} titik berkoordinat`
                  : `${outlet_map.total_valid.toLocaleString("id-ID")} titik berkoordinat`}
              </span>
            )}
          </div>
          {outlet_map.points.length > 0 ? (
            <div className="mt-4">
              <OutletMap points={outlet_map.points} />
            </div>
          ) : (
            <div className="mt-4 rounded-lg border border-dashed border-hairline bg-page p-8 text-center">
              <p className="text-sm text-ink-muted">
                Belum ada data koordinat outlet untuk akun ini.
              </p>
            </div>
          )}
        </section>

        {/* Outlet photo gallery (P1) — rendered only when outlets have photos */}
        {outlet_photos.length > 0 && (
          <section className="mt-6 rounded-lg border border-hairline bg-surface p-5">
            <div className="flex items-center gap-2 text-sm font-medium text-ink-muted">
              <ImageIcon className="h-4 w-4" />
              Foto Outlet
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {outlet_photos.map((photo, i) => (
                <figure
                  key={i}
                  className="overflow-hidden rounded-lg border border-hairline bg-page"
                >
                  <img
                    src={photo.image_url}
                    alt={photo.name ?? "Foto outlet"}
                    loading="lazy"
                    className="aspect-video w-full object-cover"
                  />
                  {photo.name && (
                    <figcaption className="truncate px-2 py-1.5 text-xs text-ink-muted">
                      {photo.name}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          </section>
        )}

        {/* Product directives (Feature 40) */}
        <section className="mt-6 rounded-lg border border-hairline bg-surface p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-ink-muted">
                <FileWarning className="h-4 w-4" />
                Peringatan Produk
              </div>
              <p className="mt-1 text-sm text-ink-body">
                {directives_summary.open_count > 0 ? (
                  <>
                    <strong>{directives_summary.open_count}</strong> peringatan aktif
                    {directives_summary.nearest_deadline && (
                      <>
                        {" "}· tenggat terdekat{" "}
                        <strong>
                          {new Date(directives_summary.nearest_deadline).toLocaleDateString("id-ID", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </strong>
                        {(() => {
                          const c = countdown(directives_summary.nearest_deadline)
                          return c ? (
                            <Badge tone={c.tone} className="ml-2 align-middle">
                              {c.label}
                            </Badge>
                          ) : null
                        })()}
                      </>
                    )}
                  </>
                ) : directives_summary.total_count > 0 ? (
                  "Tidak ada peringatan aktif."
                ) : (
                  "Belum ada peringatan tercatat untuk akun ini."
                )}
              </p>
            </div>
            <div className="flex gap-2">
              {directives_summary.total_count > 0 && (
                <Button asChild variant="secondary">
                  <Link href={`/admin/accounts/directives?account_id=${account.id}`}>
                    Lihat semua
                  </Link>
                </Button>
              )}
              <Button asChild>
                <Link href={`/admin/accounts/directives/new?account_id=${account.id}`}>
                  <Plus className="mr-2 h-4 w-4" />
                  Tambah Peringatan
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Scores & combination alerts */}
        <section className="mt-6">
          <div>
            <h2 className="text-base font-semibold text-ink-display">Skor & Peringatan</h2>
            <p className="mt-0.5 text-sm text-ink-muted">
              Skor Peluang & Risiko dihitung dari sinyal yang disetujui (berbobot
              magnitudo × keyakinan × peluruhan waktu); peringatan kombinasi memicu dari
              pasangan indikator awal.
            </p>
          </div>

          <div className="mt-4 grid gap-6 sm:grid-cols-2">
            <ScoreCard
              title="Peluang"
              tone="success"
              icon={<TrendingUp className="h-4 w-4" />}
              data={scores.opportunity}
            />
            <ScoreCard
              title="Risiko"
              tone="danger"
              icon={<TrendingDown className="h-4 w-4" />}
              data={scores.risk}
            />
          </div>

          {alerts.length > 0 ? (
            <div className="mt-6 flex flex-col gap-3">
              {alerts.map((a) => (
                <Alert
                  key={a.key}
                  tone={a.tone}
                  title={a.name}
                  icon={
                    a.polarity === "opportunity" ? (
                      <TrendingUp className="h-4 w-4" />
                    ) : (
                      <ShieldAlert className="h-4 w-4" />
                    )
                  }
                >
                  <p>{a.description}</p>
                  <p className="mt-1 text-xs opacity-80">
                    {a.matched_count} sinyal indikator awal memicu peringatan ini.
                  </p>
                </Alert>
              ))}
            </div>
          ) : (
            <p className="mt-6 rounded-lg border border-dashed border-hairline bg-surface p-4 text-sm text-ink-muted">
              Tidak ada peringatan kombinasi yang aktif. Peringatan muncul saat pasangan
              indikator awal yang relevan disetujui bersamaan.
            </p>
          )}
        </section>

        {/* External research engine (M5) */}
        <ResearchPanel
          accountId={account.id}
          research={research}
          domains={research.research_domains}
          pendingResearchCount={
            signals.pending.filter((s) => s.origin === "auto_research").length
          }
        />

        {/* Timeline */}
        <section className="mt-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-base font-semibold text-ink-display">Timeline Sinyal</h2>
              <p className="mt-0.5 text-sm text-ink-muted">
                Intelijen 360° bertanggal & bersumber. Sinyal yang disetujui menjadi SOT resmi.
              </p>
            </div>
            <Button onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Tambah sinyal
            </Button>
          </div>

          {/* Filters */}
          <div className="mt-4 flex flex-wrap items-end gap-3">
            <div className="w-full sm:w-64">
              <label htmlFor="filter-domain">Filter domain</label>
              <Select
                id="filter-domain"
                className="mt-1"
                value={signal_filters.domain ?? ""}
                onChange={(e) => applyFilter("domain", e.target.value)}
              >
                <option value="">Semua domain</option>
                {signal_filter_options.domains.map((d) => (
                  <option key={d} value={d}>
                    {domainLabel(d)}
                  </option>
                ))}
              </Select>
            </div>
            <div className="w-full sm:w-64">
              <label htmlFor="filter-place">Filter tempat</label>
              <Select
                id="filter-place"
                className="mt-1"
                value={signal_filters.place ?? ""}
                onChange={(e) => applyFilter("place", e.target.value)}
              >
                <option value="">Semua tempat</option>
                {signal_filter_options.places.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {/* Pending review */}
          {signals.pending.length > 0 && (
            <div className="mt-6 rounded-lg border border-signal bg-signal-faded p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-ink-body">Menunggu review</span>
                <Badge tone="muted">{signals.pending.length}</Badge>
                <div className="ml-auto flex items-center gap-3">
                  <label className="flex items-center gap-2 font-normal text-ink-body">
                    <Checkbox
                      checked={allPendingSelected}
                      onChange={toggleAllPending}
                      aria-label="Pilih semua"
                    />
                    <span className="text-xs">Pilih semua</span>
                  </label>
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={selected.size === 0}
                    onClick={() => setBulkOpen(true)}
                  >
                    <Trash2 className="mr-1 h-3.5 w-3.5" />
                    Hapus terpilih{selected.size > 0 ? ` (${selected.size})` : ""}
                  </Button>
                </div>
              </div>
              <p className="mt-0.5 text-xs text-ink-muted">
                Sinyal ini belum resmi dan tidak masuk timeline SOT hingga disetujui.
              </p>
              <div className="mt-4 flex flex-col gap-3">
                {signals.pending.map((s) => (
                  <SignalCard
                    key={s.id}
                    signal={s}
                    selected={selected.has(s.id)}
                    onToggleSelect={() => toggleOne(s.id)}
                    onEdit={() => openEdit(s)}
                    onDelete={() => destroy(s)}
                    onApprove={() => approve(s)}
                    onReject={() => setRejectTarget(s)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Approved timeline */}
          <div className="mt-6 flex flex-col gap-3">
            {signals.approved.length > 0 ? (
              signals.approved.map((s) => (
                <SignalCard
                  key={s.id}
                  signal={s}
                  onEdit={() => openEdit(s)}
                  onDelete={() => destroy(s)}
                />
              ))
            ) : (
              <div className="rounded-lg border border-dashed border-hairline bg-surface p-8 text-center">
                <p className="text-sm text-ink-muted">
                  Belum ada sinyal resmi.{" "}
                  {signal_filters.domain || signal_filters.place
                    ? "Coba ubah filter, atau tambahkan sinyal baru."
                    : "Tambahkan sinyal pertama untuk akun ini."}
                </p>
              </div>
            )}
          </div>
        </section>
      </AppShell>

      <SignalFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        accountId={account.id}
        taxonomy={signal_taxonomy}
        signal={editing}
      />

      <Dialog open={rejectTarget !== null} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Tolak sinyal?</DialogTitle>
            <DialogDescription>
              Sinyal “{rejectTarget?.title}” akan ditandai ditolak dan dikeluarkan dari SOT.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectTarget(null)}>
              Batal
            </Button>
            <Button variant="danger" onClick={confirmReject}>
              Tolak
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Hapus {selected.size} sinyal terpilih?</DialogTitle>
            <DialogDescription>
              {selected.size} sinyal yang menunggu review akan dihapus permanen. Tindakan ini
              tidak bisa dibatalkan.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBulkOpen(false)}>
              Batal
            </Button>
            <Button variant="danger" onClick={bulkDestroy}>
              Hapus {selected.size} sinyal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function SignalCard({
  signal,
  selected,
  onToggleSelect,
  onEdit,
  onDelete,
  onApprove,
  onReject,
}: {
  signal: Signal
  selected?: boolean
  onToggleSelect?: () => void
  onEdit: () => void
  onDelete: () => void
  onApprove?: () => void
  onReject?: () => void
}) {
  const dateLabel = signal.source_date
    ? new Date(signal.source_date).toLocaleDateString("id-ID", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "Tanpa tanggal"

  // Event period (P7) — the month the signal's event/condition pertains to,
  // distinct from the source's publish date above.
  const periodLabel = signal.event_month
    ? new Date(`${signal.event_month}-01T00:00:00`).toLocaleDateString("id-ID", {
        year: "numeric",
        month: "short",
      })
    : null

  return (
    <article
      className={cn(
        "rounded-lg border bg-surface p-4",
        selected ? "border-danger ring-1 ring-danger" : "border-hairline",
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        {onToggleSelect && (
          <Checkbox
            checked={Boolean(selected)}
            onChange={onToggleSelect}
            aria-label={`Pilih sinyal ${signal.title}`}
          />
        )}
        <Badge tone="neutral">{domainLabel(signal.domain)}</Badge>
        {signal.polarity && (
          <Badge tone={polarityTone(signal.polarity)}>
            {POLARITY_LABELS[signal.polarity] ?? signal.polarity}
          </Badge>
        )}
        {signal.horizon && (
          <Badge tone="muted">{HORIZON_LABELS[signal.horizon] ?? signal.horizon}</Badge>
        )}
        {signal.impact_axis && (
          <Badge tone="muted">
            {IMPACT_AXIS_LABELS[signal.impact_axis] ?? signal.impact_axis}
          </Badge>
        )}
        {signal.is_structural && <Badge tone="signal">Struktural</Badge>}
        {signal.source_date ? (
          <span className="ml-auto text-xs text-ink-muted">{dateLabel}</span>
        ) : (
          <Badge
            tone="neutral"
            className="ml-auto inline-flex items-center gap-1"
            title="Sinyal tanpa tanggal sumber — verifikasi sebelum dipercaya"
          >
            <CalendarOff className="h-3 w-3" />
            Tanpa tanggal
          </Badge>
        )}
      </div>

      <h3 className="mt-3 text-sm font-semibold text-ink-display">{signal.title}</h3>
      {signal.aspect && (
        <p className="mt-0.5 text-xs text-ink-muted">{aspectLabel(signal.aspect)}</p>
      )}
      {signal.body && <p className="mt-2 text-sm text-ink-body">{signal.body}</p>}

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-muted">
        {periodLabel && (
          <span className="inline-flex items-center gap-1" title="Periode kejadian">
            <CalendarClock className="h-3 w-3" />
            Periode {periodLabel}
          </span>
        )}
        {signal.geo_place && (
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            {signal.geo_place}
          </span>
        )}
        {(signal.magnitude != null || signal.confidence != null) && (
          <span>
            Mag {signal.magnitude ?? "—"} · Yakin{" "}
            {signal.confidence != null ? signal.confidence.toFixed(2) : "—"}
          </span>
        )}
        {signal.source_url && (
          <a
            href={signal.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-accent hover:underline"
          >
            <ExternalLink className="h-3 w-3" />
            Sumber
          </a>
        )}
        {signal.source_status === "unreachable" && (
          <Badge
            tone="danger"
            className="inline-flex items-center gap-1"
            title="Tautan sumber tidak dapat dijangkau saat diperiksa (404 / paywall / tidak resolve)"
          >
            <Unlink className="h-3 w-3" />
            Sumber tak terjangkau
          </Badge>
        )}
        {signal.reviewed_by_name && (
          <span>Direview oleh {signal.reviewed_by_name}</span>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-hairline pt-3">
        {onApprove && (
          <Button size="sm" variant="secondary" onClick={onApprove}>
            <Check className="mr-1 h-3.5 w-3.5" />
            Setujui
          </Button>
        )}
        {onReject && (
          <Button size="sm" variant="ghost" onClick={onReject}>
            <X className="mr-1 h-3.5 w-3.5" />
            Tolak
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={onEdit}>
          <Pencil className="mr-1 h-3.5 w-3.5" />
          Edit
        </Button>
        <Button size="sm" variant="ghost" onClick={onDelete}>
          <Trash2 className="mr-1 h-3.5 w-3.5" />
          Hapus
        </Button>
      </div>
    </article>
  )
}

function ScoreCard({
  title,
  tone,
  icon,
  data,
}: {
  title: string
  tone: "success" | "danger"
  icon: React.ReactNode
  data: PolarityScore
}) {
  return (
    <div className="rounded-lg border border-hairline bg-surface p-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-ink-muted">
          <span className="text-ink-muted">{icon}</span>
          {title}
        </div>
        <Badge tone={tone}>{data.count} sinyal</Badge>
      </div>
      <div className="mt-2 text-2xl font-semibold text-ink-body">{data.total.toFixed(1)}</div>

      {data.by_axis.length > 0 ? (
        <dl className="mt-4 flex flex-col gap-2 border-t border-hairline pt-3">
          {data.by_axis.map((a) => (
            <div key={a.axis} className="flex items-center justify-between text-sm">
              <dt className="text-ink-muted">{impactAxisLabel(a.axis)}</dt>
              <dd className="font-medium text-ink-body">{a.score.toFixed(1)}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="mt-4 border-t border-hairline pt-3 text-sm text-ink-muted">
          Belum ada sinyal {title.toLowerCase()} yang disetujui.
        </p>
      )}
    </div>
  )
}

// Deterministic, props-only date formatting (SSR-safe — no Date.now()).
function fmtPicDate(iso: string) {
  return new Date(iso).toLocaleDateString("id-ID", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

function PicHistory({ assignments }: { assignments: PicAssignment[] }) {
  return (
    <div className="mt-4 border-t border-hairline pt-4">
      <div className="flex items-center gap-2 text-sm font-medium text-ink-muted">
        <History className="h-4 w-4" />
        Riwayat PIC
      </div>

      {assignments.length > 0 ? (
        <ol className="mt-4 flex flex-col gap-3">
          {assignments.map((a) => (
            <li key={a.id} className="rounded-lg border border-hairline bg-page p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-ink-body">{a.pic_name ?? "—"}</span>
                {a.pic_job_title && (
                  <span className="text-xs text-ink-muted">{a.pic_job_title}</span>
                )}
                {a.is_current && (
                  <Badge tone="success" className="ml-auto">
                    PIC saat ini
                  </Badge>
                )}
              </div>
              <div className="mt-1 text-sm text-ink-body">
                {fmtPicDate(a.started_at)} →{" "}
                {a.ended_at ? fmtPicDate(a.ended_at) : "Sekarang"}
              </div>
              {a.assigned_by_name && (
                <div className="mt-1 text-xs text-ink-muted">
                  Ditugaskan oleh {a.assigned_by_name}
                </div>
              )}
              {a.note && <p className="mt-2 text-sm text-ink-body">{a.note}</p>}
            </li>
          ))}
        </ol>
      ) : (
        <p className="mt-4 text-sm text-ink-muted">Belum ada riwayat PIC.</p>
      )}
    </div>
  )
}

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-xs text-ink-muted">{label}</dt>
      <dd className="mt-0.5 text-ink-body">{value ?? "—"}</dd>
    </div>
  )
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="rounded-full bg-page p-2 text-ink-muted">{icon}</div>
      <div>
        <div className="text-xl font-semibold text-ink-body">{value}</div>
        <div className="text-xs text-ink-muted">{label}</div>
      </div>
    </div>
  )
}
